import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Siren } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
interface EmergencyCallNotificationProps {
  callerName: string;
  onTap: () => void;
}

/**
 * OS-style notification-bar card for an emergency call. Mirrors the system
 * notification look (app icon, app name, timestamp) but flagged in red so the
 * urgency reads instantly from the lock screen / notification shade.
 */
export function EmergencyCallNotification({
  callerName,
  onTap
}: EmergencyCallNotificationProps) {
  const {
    isLight
  } = useTheme();
  return <motion.button type="button" onClick={onTap} initial={{
    opacity: 0,
    y: -26,
    scale: 0.96
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    y: -26,
    scale: 0.96
  }} transition={{
    type: 'spring',
    stiffness: 380,
    damping: 30
  }} className={`absolute top-14 left-3 right-3 z-[62] w-auto overflow-hidden rounded-[20px] text-left shadow-xl transition-transform active:scale-[0.985] ${isLight ? 'bg-white/95 ring-1 ring-red-200 shadow-red-900/10 backdrop-blur-2xl' : 'bg-[#1c1517]/95 ring-1 ring-red-500/25 shadow-black/50 backdrop-blur-2xl'}`} data-id="element-491" data-mp="v5uke9bgamez">
      <div className={`flex items-center gap-1.5 px-3 py-1 ${isLight ? 'bg-red-600' : 'bg-red-600/90'}`} data-id="element-492" data-mp="1fytibbl190ak">
        <motion.span animate={{
        opacity: [1, 0.35, 1]
      }} transition={{
        duration: 1.1,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="flex items-center gap-1.5" data-id="element-493" data-mp="1b1jwe5wur6rh">
          <Siren className="h-3 w-3 text-white" data-id="element-494" data-mp="jbf1hdbgv1c0" />
          <span className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-white" data-id="element-495" data-mp="x9wp7b1dgdtx">
            Emergency
          </span>
        </motion.span>
      </div>

      <div className="flex items-center gap-3 p-3" data-id="element-496" data-mp="scn1bztmsa87">
        <div className="relative flex-shrink-0" data-id="element-497" data-mp="28nha7q0aenx3">
          <div className="grid h-9 w-9 place-items-center rounded-[10px] bg-red-600 shadow-sm shadow-red-600/30" data-id="element-498" data-mp="28qd8xgjylgml">
            <Siren className="h-5 w-5 text-white" strokeWidth={2.2} data-id="element-499" data-mp="2bqf04ywhnhlm" />
          </div>
          <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5" data-id="element-500" data-mp="2bshlh5mp4mk0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-70" data-id="element-501" data-mp="2fpxa4zmru89o" />
            <span className={`relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ${isLight ? 'ring-white' : 'ring-[#1c1517]'}`} data-id="element-502" data-mp="2kp0f6k2r4ehn" />
          </span>
        </div>

        <div className="min-w-0 flex-1" data-id="element-503" data-mp="1s0bakb2hlr15">
          <div className="flex items-center justify-between gap-2" data-id="element-504" data-mp="1x0h827vfu7r5">
            <span className={`truncate text-[13px] font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-505" data-mp="220bxe6zkepa3">
              Vexu
            </span>
            <span className={`flex-shrink-0 text-[10px] font-medium ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-506" data-mp="26sd8vqowsbz2">
              now
            </span>
          </div>
          <p className={`mt-0.5 truncate text-[12.5px] font-semibold leading-snug ${isLight ? 'text-red-600' : 'text-red-400'}`} data-id="element-507" data-mp="1e8wtwaw1y1x9">
            Emergency call from {callerName}
          </p>
          <p className={`mt-0.5 truncate text-[11.5px] leading-snug ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-508" data-mp="11qavprd46m2z">
            Your AI flagged this as urgent — tap to listen live.
          </p>
        </div>

        <ChevronRight className={`h-4 w-4 flex-shrink-0 ${isLight ? 'text-red-300' : 'text-red-500/50'}`} data-id="element-509" data-mp="nsw4ch3aw48d" />
      </div>
    </motion.button>;
}